import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { cn } from '../../lib/utils';
import { destinationById } from '../../lib/publishing/destinations';
import { buildGbpEvent, gbpEventToEndInput, gbpEventToStartInput } from '../../lib/publishing/gbpDate';
import {
  captionOverrideTickPatch,
  frozenCaptionWarning,
  hasOwnCaption,
} from '../../lib/publishing/captionOverride';
import type { GbpConfig, GbpCtaType, GbpTopicType, PostPlatform } from '../../types/publishing';
import type { ComposerTarget } from './composerState';
import { MediaPicker } from './MediaPicker';

// Publishing composer §D — ONE options block per ticked destination, COLLAPSED
// BY DEFAULT and headed with its channel logo + name.
//
// Inside: three tick-boxes that reveal their control when enabled (Caption
// override / Media override / Add first comment) plus the destination's existing
// platform specifics (FB feed-vs-Reel, IG feed-vs-Reel, GBP topic/CTA/event/offer).
//
// Collapsed-by-default is the point of the redesign: the common case is one
// caption to every destination, and the old always-expanded per-network cards
// made that case look like five forms to fill in. The header summarises what is
// customised so a collapsed block never hides a surprise.

function Segmented<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 p-0.5">
      {options.map((o) => (
        <Button
          key={o.value}
          type="button"
          size="sm"
          variant={value === o.value ? 'default' : 'ghost'}
          disabled={disabled}
          className="h-7 px-2.5 text-xs"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}

// A tick-box that reveals its control only when enabled.
function ToggleField({
  id,
  label,
  checked,
  disabled,
  hint,
  onCheckedChange,
  children,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  hint?: string;
  onCheckedChange: (next: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(c) => onCheckedChange(c === true)}
        />
        <label htmlFor={id} className="text-sm text-slate-700">
          {label}
        </label>
        {hint && <span className="text-xs text-slate-400">{hint}</span>}
      </div>
      {checked && <div className="mt-2 pl-6">{children}</div>}
    </div>
  );
}

export function DestinationOptionsCard({
  target,
  baseCaption,
  disabled,
  onChange,
  title,
  iconChannels,
}: {
  target: ComposerTarget;
  // The main caption, so ticking "Caption override" can prefill with it.
  baseCaption: string;
  disabled?: boolean;
  onChange: (next: ComposerTarget) => void;
  // Presentational overrides for the COMBINED Story block (both stories ticked):
  // one card headed "Story" with both channel glyphs, editing a representative
  // target whose changes the caller fans out to both. Default = the target's own
  // label + single platform icon.
  title?: string;
  iconChannels?: PostPlatform[];
}) {
  const destination = destinationById(target.destination);
  const heading = title ?? destination.label;
  const icons = iconChannels ?? [destination.platform];
  // Stories publish NO caption field (FB Page Stories + IG Stories), so a caption
  // override is dead UI on a story destination — dropped below. The combined
  // "Story" block also lands here (its representative target is a story).
  const isStory = destination.placement === 'story';
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<ComposerTarget>) => onChange({ ...target, ...patch });

  // What the collapsed header advertises, so nothing customised is hidden.
  const summary: string[] = [];
  if (!isStory && target.overrideCaption && target.customContent.trim() !== '') summary.push('custom caption');
  if (target.overrideMedia && target.customMedia.length > 0) summary.push('custom media');
  if (destination.supportsFirstComment && target.addFirstComment && target.firstComment.trim() !== '') {
    summary.push('first comment');
  }
  if (destination.platform === 'facebook' && destination.placement === 'feed' && target.fbContentType === 'reel') {
    summary.push('Reel');
  }
  if (destination.platform === 'instagram' && destination.placement === 'feed' && target.igContentType === 'reel') {
    summary.push('Reel');
  }
  if (destination.platform === 'googlebusiness' && target.gbp.topicType !== 'STANDARD') {
    summary.push(target.gbp.topicType === 'EVENT' ? 'event' : 'offer');
  }

  const fieldId = (suffix: string) => `${target.destination}-${suffix}`;
  // The frozen-copy warning (src/lib/publishing/captionOverride.ts) — shown on
  // the block whether collapsed or open, since the collapsed header is exactly
  // where it would otherwise be missed.
  const ownCaption = hasOwnCaption(target.destination, target);

  return (
    <div className="rounded-lg border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        )}
        {icons.map((ch) => (
          <ChannelIcon key={ch} channel={ch} className="h-4 w-4" />
        ))}
        <span className="text-sm font-medium text-slate-900">{heading}</span>
        {summary.length > 0 && (
          <span className="ml-auto truncate rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            {summary.join(' · ')}
          </span>
        )}
      </button>
      {ownCaption && (
        <p className="border-t border-amber-100 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
          {frozenCaptionWarning(heading)}
        </p>
      )}

      {open && (
        <div className={cn('space-y-4 border-t border-slate-100 px-3 py-3', disabled && 'opacity-60')}>
          {/* placement sub-choice — only on the FEED destinations (a story's
              content type is fixed by the destination itself) */}
          {destination.platform === 'facebook' && destination.placement === 'feed' && (
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Format</p>
              <Segmented
                options={[
                  { value: 'feed', label: 'Feed post' },
                  { value: 'reel', label: 'Reel' },
                ]}
                value={target.fbContentType === 'reel' ? 'reel' : 'feed'}
                disabled={disabled}
                onChange={(v) => set({ fbContentType: v })}
              />
            </div>
          )}
          {destination.platform === 'instagram' && destination.placement === 'feed' && (
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Format</p>
              <Segmented
                options={[
                  { value: 'feed', label: 'Feed post' },
                  { value: 'reel', label: 'Reel' },
                ]}
                value={target.igContentType === 'reel' ? 'reel' : 'feed'}
                disabled={disabled}
                onChange={(v) => set({ igContentType: v })}
              />
              {/* The IG asymmetry is worth stating where the choice is made. */}
              <p className="mt-1 text-[11px] text-slate-400">
                Instagram decides feed vs Reel from the media — a video always publishes as a Reel.
              </p>
            </div>
          )}
          {destination.platform === 'googlebusiness' && (
            <GbpFields gbp={target.gbp} disabled={disabled} onChange={(gbp) => set({ gbp })} />
          )}

          {/* Stories publish no caption — no caption override on a story block. */}
          {!isStory && (
            <ToggleField
              id={fieldId('caption')}
              label="Caption override"
              checked={target.overrideCaption}
              disabled={disabled}
              onCheckedChange={(checked) =>
                set(captionOverrideTickPatch(target, checked, baseCaption))
              }
            >
              <Textarea
                value={target.customContent}
                disabled={disabled}
                rows={3}
                placeholder={`Caption for ${heading} only`}
                onChange={(e) => set({ customContent: e.target.value })}
              />
            </ToggleField>
          )}

          <ToggleField
            id={fieldId('media')}
            label="Media override"
            checked={target.overrideMedia}
            disabled={disabled}
            onCheckedChange={(overrideMedia) => set({ overrideMedia })}
          >
            <MediaPicker
              media={target.customMedia}
              disabled={disabled}
              onChange={(customMedia) => set({ customMedia })}
            />
          </ToggleField>

          {destination.supportsFirstComment && (
            <ToggleField
              id={fieldId('first-comment')}
              label="Add first comment"
              checked={target.addFirstComment}
              disabled={disabled}
              onCheckedChange={(addFirstComment) => set({ addFirstComment })}
            >
              <Input
                value={target.firstComment}
                disabled={disabled}
                placeholder="Posted as the first comment"
                onChange={(e) => set({ firstComment: e.target.value })}
              />
            </ToggleField>
          )}
        </div>
      )}
    </div>
  );
}

const CTA_TYPES: GbpCtaType[] = ['LEARN_MORE', 'BOOK', 'ORDER', 'SHOP', 'SIGN_UP', 'CALL'];

function GbpFields({
  gbp,
  onChange,
  disabled,
}: {
  gbp: GbpConfig;
  onChange: (g: GbpConfig) => void;
  disabled?: boolean;
}) {
  const set = (patch: Partial<GbpConfig>) => onChange({ ...gbp, ...patch });

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1 text-xs font-medium text-slate-500">Post type</p>
        <Segmented<GbpTopicType>
          options={[
            { value: 'STANDARD', label: 'Update' },
            { value: 'EVENT', label: 'Event' },
            { value: 'OFFER', label: 'Offer' },
          ]}
          value={gbp.topicType}
          disabled={disabled}
          onChange={(topicType) => set({ topicType })}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <select
          value={gbp.cta?.type ?? ''}
          disabled={disabled}
          aria-label="Call to action"
          className="h-9 rounded-md border border-slate-200 px-2 text-sm"
          onChange={(e) =>
            set({
              cta: e.target.value
                ? { type: e.target.value as GbpCtaType, url: gbp.cta?.url ?? '' }
                : null,
            })
          }
        >
          <option value="">No button</option>
          {CTA_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        {gbp.cta && (
          <Input
            value={gbp.cta.url}
            disabled={disabled}
            placeholder="Button URL"
            onChange={(e) => set({ cta: { type: gbp.cta!.type, url: e.target.value } })}
          />
        )}
      </div>

      {gbp.topicType === 'EVENT' && <GbpEventFields gbp={gbp} onChange={onChange} disabled={disabled} />}
      {gbp.topicType === 'OFFER' && (
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={gbp.offer?.couponCode ?? ''}
            disabled={disabled}
            placeholder="Coupon code"
            onChange={(e) => set({ offer: { ...gbp.offer, couponCode: e.target.value } })}
          />
          <Input
            value={gbp.offer?.redeemOnlineUrl ?? ''}
            disabled={disabled}
            placeholder="Redeem URL"
            onChange={(e) => set({ offer: { ...gbp.offer, redeemOnlineUrl: e.target.value } })}
          />
        </div>
      )}
    </div>
  );
}

function GbpEventFields({
  gbp,
  onChange,
  disabled,
}: {
  gbp: GbpConfig;
  onChange: (g: GbpConfig) => void;
  disabled?: boolean;
}) {
  const ev = gbp.event;
  const startInput = ev ? gbpEventToStartInput(ev) : '';
  const endInput = ev ? gbpEventToEndInput(ev) : '';

  function rebuild(next: { title?: string; startIso?: string; endIso?: string }) {
    const title = next.title ?? ev?.title ?? '';
    const startIso = next.startIso ?? startInput;
    const endIso = next.endIso ?? endInput;
    const built = buildGbpEvent({ title, startIso, endIso, includeTimes: true });
    // Keep a partial event so typing one field at a time doesn't wipe the others.
    onChange({
      ...gbp,
      event: built ?? {
        title,
        startDate: { year: 0, month: 1, day: 1 },
        endDate: { year: 0, month: 1, day: 1 },
      },
    });
  }

  return (
    <div className="space-y-2 rounded-md bg-slate-50 p-2">
      <Input
        value={ev?.title ?? ''}
        disabled={disabled}
        placeholder="Event title"
        onChange={(e) => rebuild({ title: e.target.value })}
      />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-500">
          Starts
          <input
            type="datetime-local"
            value={startInput}
            disabled={disabled}
            className="mt-0.5 block h-9 w-full rounded-md border border-slate-200 px-2 text-sm"
            onChange={(e) => rebuild({ startIso: e.target.value })}
          />
        </label>
        <label className="text-xs text-slate-500">
          Ends
          <input
            type="datetime-local"
            value={endInput}
            disabled={disabled}
            className="mt-0.5 block h-9 w-full rounded-md border border-slate-200 px-2 text-sm"
            onChange={(e) => rebuild({ endIso: e.target.value })}
          />
        </label>
      </div>
    </div>
  );
}
